import equal from 'fast-deep-equal'
import debounce from 'lodash.debounce'
import { useEffect, useMemo, useRef } from 'react'

import { useUpdateSavedItemBookmarkHandlers } from './useUpdateSavedItemSnackbarHandlers'

import { ExperienceOfferPageState } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import { useCurrentExperiencePageTicketOptions } from 'hooks/ExperiencePage/useCurrentExperiencePageTicketOptions'
import { useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import usePrevious from 'hooks/usePrevious'
import { useUpdateTripItem } from '../api/tripItem'
import { useTrip } from '../api/trip'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import { ExperienceItem, SavedExperienceData } from 'tripPlanner/types/tripItem'
import { createExperienceItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/experience'

/**
 * If the experience is bookmarked and the user changes date/time/etc. on the experience page,
 * this hook automatically updates the trip item with those changes.
 */
export function useSyncExperienceBookmarkWithPageState(
  experience: App.ExperienceOffer,
  experiencePageState: ExperienceOfferPageState,
) {
  const { currentTripId, currentRegionCode, currencyCode } = useAppSelector(
    (state: App.State) => ({
      currentTripId: getImmersiveTripId(state),
      currentRegionCode: state.geo.currentRegionCode,
      currencyCode: state.geo.currentCurrency,
    }),
    shallowEqual,
  )

  const { data: trip } = useTrip({ tripId: currentTripId })

  const { showUpdateSuccessSnackbar } = useUpdateSavedItemBookmarkHandlers()

  const { mutate: updateMutate } = useUpdateTripItem({
    onSuccess: (item) => {
      showUpdateSuccessSnackbar(
        'Details updated for saved offer',
        item.tripId,
        item.id,
      )
    },
  })

  const allTicketOptions = useCurrentExperiencePageTicketOptions(
    experience.id,
    experiencePageState,
  )

  /*
    Note: The use of ref here is to ensure that the debounced update function
    always has access to the latest version of the bookmark when it runs.
  */
  const bookmarkRef = useRef<ExperienceItem>(undefined)
  bookmarkRef.current = trip?.items.find(
    (item): item is ExperienceItem =>
      item.type === 'EXPERIENCE' &&
      !item.isBooked &&
      item.code === experience.id,
  )

  const previousState = usePrevious(experiencePageState, experiencePageState)
  // Check if state is deeply equal to previous - we don't want to do updates if we got
  // a new object reference but the state hasn't actually changed meaningfully
  const pageStateChanged = !equal(experiencePageState, previousState)

  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (
          experience: App.ExperienceOffer,
          experienceOfferPageState: ExperienceOfferPageState,
          allTicketOptions: Array<App.ExperienceItemTicket>,
        ) => {
          const bookmarkItem = bookmarkRef.current
          if (!bookmarkItem) {
            return
          }

          const itemPayload = createExperienceItemPayload({
            experience,
            currentRegionCode,
            currencyCode,
            experienceOfferPageState,
            allTicketOptions,
          })

          if (
            currentTripId &&
            itemPayload.savedItemData &&
            bookmarkItem.savedItemData &&
            isAtLeastAsSpecificAsPrevious(
              itemPayload.savedItemData,
              bookmarkItem.savedItemData,
            )
          ) {
            const updatePayload: Parameters<
              typeof updateMutate
            >[0]['tripItem'] = {
              ...itemPayload,
              notes: bookmarkItem.notes,
            }
            updateMutate({
              tripId: currentTripId,
              tripItemId: bookmarkItem.id,
              tripItem: updatePayload,
            })
          }
        },
        2000,
      ),
    [currencyCode, currentTripId, currentRegionCode, updateMutate],
  )

  useEffect(() => {
    if (pageStateChanged) {
      debouncedUpdate(experience, experiencePageState, allTicketOptions)
    }
  })
}
function isAtLeastAsSpecificAsPrevious(
  newData: SavedExperienceData,
  prevData: SavedExperienceData,
) {
  return (
    savedDataSpecificityLevel(newData) >= savedDataSpecificityLevel(prevData)
  )
}

/**
 * Specificity of saved data:
 * 0: no dates or anything
 * 1: saved date but no time
 * 2: saved date and time
 * 3: saved ticket selections
 */
function savedDataSpecificityLevel(data?: SavedExperienceData) {
  if (typeof data === 'undefined') {
    return 0
  }

  if (data.tickets && data?.tickets.length > 0) {
    return 3
  }

  if (data.date && data.time) {
    return 2
  }

  if (data.date) {
    return 1
  }

  return 0
}
